import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Link,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import EventIcon from "@mui/icons-material/Event";
import EditNoteIcon from "@mui/icons-material/EditNote";
import StatusChip, { SeverityChip } from "../common/StatusChip";
import Timeline from "./Timeline";
import DryRunPanel from "./DryRunPanel";
import ArtifactsPanel from "./ArtifactsPanel";
import StatusProgressStepper from "./StatusProgressStepper";
import ConfirmDialog from "../common/ConfirmDialog";
import {
  approveRequests,
  cancelRequests,
  fetchRequestDetail,
  runNowRequests,
} from "../../../../store/remediation/remediationSlice";
import { friendlyErrorMessage, friendlySkippedReason } from "../common/ErrorCodeMap";
import { useRemediationSnackbar } from "../common/SnackbarHost";
import { formatAbsolute, formatRelative, useActionFeedback } from "../common/hooks";
import { NON_ACTIONABLE_STATUSES } from "../common/statusColors";

const APPROVE_ACK =
  "I understand that approving these requests will schedule automated changes to my AWS infrastructure.";
const RUN_NOW_CONFIRM = "REMEDIATE NOW";

const TABS = ["overview", "artifacts", "timeline"];

const RequestDetailDrawer = ({ open, requestId, onClose, onRequestRefetch, onReschedule, onGoToIamTab }) => {
  const dispatch = useDispatch();
  const { showSnackbar } = useRemediationSnackbar();
  const detail = useSelector((s) =>
    requestId != null ? s.remediation.detailById[requestId] : null
  );
  const detailStatus = useSelector((s) => s.remediation.detailStatus);
  const actionInProgress = useSelector((s) => s.remediation.actionInProgress);
  const [activeTab, setActiveTab] = useState("overview");
  const [confirm, setConfirm] = useState(null); // null | "approve" | "cancel" | "run-now"
  const submitting = Boolean(
    actionInProgress?.approve ||
    actionInProgress?.cancel ||
    actionInProgress?.["run-now"]
  );
  const loading = detailStatus?.status === "loading" && detailStatus?.id === requestId;

  const load = () => {
    if (requestId == null) return;
    dispatch(fetchRequestDetail(requestId));
  };

  useEffect(() => {
    if (open && requestId != null) {
      setActiveTab("overview");
      dispatch(fetchRequestDetail(requestId));
    }
  }, [open, requestId, dispatch]);

  const status = detail?.status;
  const canApprove = status === "DRY_RUN_READY";
  const canCancel = status && !NON_ACTIONABLE_STATUSES.has(status);
  const canRunNow = status === "APPROVED";
  const canReschedule = status && !NON_ACTIONABLE_STATUSES.has(status);

  // Friendly past-tense verb per action kind for the success toast.
  const verbByKind = {
    approve: "Approved",
    cancel: "Cancelled",
    "run-now": "Run-now scheduled for",
  };

  const dispatchAction = (kind, body) => {
    if (kind === "approve") dispatch(approveRequests(body));
    else if (kind === "cancel") dispatch(cancelRequests(body));
    else if (kind === "run-now") dispatch(runNowRequests(body));
  };

  useActionFeedback(
    ["approve", "cancel", "run-now"],
    ({ kind, succeeded = [], skipped = [], error }) => {
      if (!open) return;
      const verb = verbByKind[kind] || kind;
      if (error) {
        showSnackbar({ message: friendlyErrorMessage(error), severity: "error" });
        return;
      }
      if (succeeded.length && skipped.length === 0) {
        showSnackbar({
          message: `${verb} ${succeeded.length === 1 ? "request" : `${succeeded.length} requests`}.`,
          severity: "success",
        });
      } else if (succeeded.length && skipped.length) {
        showSnackbar({
          message: `${verb} ${succeeded.length}, skipped ${skipped.length}.`,
          severity: "warning",
          title: "Partial success",
          details: skipped.map((s) => `#${s.request_id}: ${friendlySkippedReason(s)}`),
        });
      } else {
        showSnackbar({
          message: "No requests were updated.",
          severity: "warning",
          title: "All skipped",
          details: skipped.map((s) => `#${s.request_id}: ${friendlySkippedReason(s)}`),
        });
      }
      setConfirm(null);
      load();
      if (typeof onRequestRefetch === "function") onRequestRefetch();
    }
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", md: 640 },
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {(!detail || loading) && requestId != null && (
          <Box sx={{ p: 3, pb: 6, overflow: "auto", flex: 1 }}>
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="rectangular" height={140} sx={{ mt: 2 }} />
            <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
          </Box>
        )}
        {detail && (
          <Box sx={{ p: 3, pb: 6, overflow: "auto", flex: 1 }}>
            {/* Header */}
            <Stack direction="row" alignItems="flex-start" spacing={1.5} sx={{ mb: 1.5 }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Request #{detail.id} · {detail.account_name || detail.account_number}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {detail.check_title}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontFamily: "monospace" }}>
                  {detail.resource_arn}
                </Typography>
              </Box>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
              <StatusChip status={detail.status} />
              <SeverityChip severity={detail.severity} />
              <Chip
                size="small"
                label={`Attempt ${detail.attempt_count ?? 0} of ${detail.max_attempts ?? 3}`}
                sx={{ backgroundColor: "grey.100" }}
              />
              <Chip
                size="small"
                label={`Status entered ${formatRelative(detail.status_entered_at)}`}
                sx={{ backgroundColor: "grey.100" }}
              />
              {detail.scheduled_at && (
                <Chip
                  size="small"
                  icon={<EventIcon fontSize="small" />}
                  label={`Scheduled ${formatAbsolute(detail.scheduled_at)}`}
                  sx={{ backgroundColor: "grey.100" }}
                />
              )}
            </Stack>

            <StatusProgressStepper status={detail.status} />

            {/* Action bar */}
            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => setConfirm("approve")}
                disabled={!canApprove || submitting}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                size="small"
                color="warning"
                startIcon={<PlayArrowIcon />}
                onClick={() => setConfirm("run-now")}
                disabled={!canRunNow || submitting}
              >
                Run now
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EventIcon />}
                onClick={() => onReschedule && onReschedule(detail.id)}
                disabled={!canReschedule || submitting}
              >
                Re-schedule
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setConfirm("cancel")}
                disabled={!canCancel || submitting}
              >
                Cancel
              </Button>
              {detail.status === "NEEDS_INPUTS" && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditNoteIcon />}
                  onClick={() => {
                    // For now, route to the request form to re-supply inputs.
                    onClose && onClose();
                  }}
                  disabled={submitting}
                >
                  Provide inputs
                </Button>
              )}
            </Stack>

            {detail.status === "PERMS_DENIED" && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                CloudCatcher does not have enough permission to run this task. Please deploy the
                IAM stack found{" "}
                {onGoToIamTab ? (
                  <Link
                    component="button"
                    type="button"
                    underline="always"
                    onClick={() => {
                      onGoToIamTab();
                      onClose?.();
                    }}
                    sx={{
                      fontWeight: 600,
                      fontSize: "inherit",
                      verticalAlign: "baseline",
                      border: "none",
                      background: "none",
                      padding: 0,
                      cursor: "pointer",
                      color: "inherit",
                    }}
                  >
                    here
                  </Link>
                ) : (
                  "on the IAM Stack tab"
                )}
                . The worker will retry automatically after permissions are updated.
              </Alert>
            )}
            {detail.cancellation_reason && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Cancellation reason:</strong> {detail.cancellation_reason}
              </Alert>
            )}

            <Tabs
              value={activeTab}
              onChange={(_e, v) => setActiveTab(v)}
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                mb: 2,
                "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 13 },
              }}
            >
              <Tab value="overview" label="Overview" />
              <Tab value="artifacts" label="Artifacts" />
              <Tab value="timeline" label={`Timeline (${detail.audit_history?.length || 0})`} />
            </Tabs>

            {activeTab === "overview" && (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>Dry-run plan</Typography>
                <Box sx={{ mt: 0.5, mb: 2 }}>
                  <DryRunPanel preview={detail.preview_summary} />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="caption" sx={{ fontWeight: 600 }}>Bound inputs</Typography>
                <Box sx={{ mt: 0.5 }}>
                  {detail.bound_inputs && Object.keys(detail.bound_inputs).length > 0 ? (
                    <Box
                      component="pre"
                      sx={{
                        p: 1,
                        backgroundColor: "grey.50",
                        fontSize: 12,
                        borderRadius: 1,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {JSON.stringify(detail.bound_inputs, null, 2)}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">No inputs bound.</Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" spacing={2} flexWrap="wrap">
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Created</Typography>
                    <Typography variant="caption">{formatAbsolute(detail.created_at)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Created by</Typography>
                    <Typography variant="caption">{detail.created_by || detail.user_email}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Last modified</Typography>
                    <Typography variant="caption">{formatRelative(detail.last_modified_at)}</Typography>
                  </Box>
                  {detail.dormancy_last_nudged_at && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Last nudge</Typography>
                      <Typography variant="caption">{formatRelative(detail.dormancy_last_nudged_at)}</Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            )}

            {activeTab === "artifacts" && (
              <ArtifactsPanel requestId={detail.id} active={activeTab === "artifacts"} />
            )}

            {activeTab === "timeline" && <Timeline events={detail.audit_history} />}
          </Box>
        )}
      </Drawer>

      <ConfirmDialog
        open={confirm === "approve"}
        title="Approve remediation"
        description="Approving moves this request to APPROVED and schedules execution."
        mode="checkbox"
        requiredText={APPROVE_ACK}
        confirmLabel="Approve"
        confirmColor="success"
        preview={detail?.preview_summary ? <DryRunPanel preview={detail.preview_summary} /> : null}
        onClose={() => setConfirm(null)}
        onConfirm={() =>
          dispatchAction("approve", { request_ids: [detail.id], acknowledgement: APPROVE_ACK })
        }
        isSubmitting={submitting}
      />
      <ConfirmDialog
        open={confirm === "cancel"}
        title="Cancel remediation"
        description="Provide an optional reason — your team will see it on the request timeline."
        mode="reason"
        confirmLabel="Cancel request"
        confirmColor="error"
        onClose={() => setConfirm(null)}
        onConfirm={({ reason }) =>
          dispatchAction("cancel", { request_ids: [detail.id], reason: reason || null })
        }
        isSubmitting={submitting}
      />
      <ConfirmDialog
        open={confirm === "run-now"}
        title="Run remediation immediately"
        description="Bypasses the scheduled time and runs on the next worker tick."
        mode="match"
        requiredText={RUN_NOW_CONFIRM}
        confirmLabel="Run now"
        confirmColor="warning"
        onClose={() => setConfirm(null)}
        onConfirm={() =>
          dispatchAction("run-now", { request_ids: [detail.id], confirmation: RUN_NOW_CONFIRM })
        }
        isSubmitting={submitting}
      />
    </>
  );
};

export default RequestDetailDrawer;
